import {
  A_LA_CARTE_NOTE,
  EMPTY_VALUE,
  EXPIRATION_WARNING_DAYS,
  GLANCE_RESOLUTION_LABEL,
  NATIVE_NOTE,
  NO_CASE_NUMBER_LABEL,
  OPEN_DRUG_TEST_MODAL_PARAM,
  RESOLUTION_BADGE_LABEL,
  RESOLUTION_DISPLAY_LABEL,
  SCHEDULING_ERROR_BANNER,
  SCHEDULING_ERROR_STATUS,
  SOURCE_BADGE_LABEL,
  SOURCE_GLANCE_LABEL,
  WITHDRAWN_BANNER_COPY,
  WITHDRAWN_BANNER_TITLE_PREFIX,
  WITHDRAWN_GENERIC_MESSAGE,
  WITHDRAWN_STATUSES,
  WithdrawnStatus,
} from './constants';
import type {
  DrugTestReport,
  DrugTestResolution,
  DrugTestRetries,
  DrugTestSource,
  DrugTestStatusAlert,
  DrugTestTimeInStatus,
} from './types';

export const getSourceBadgeLabel = (source: DrugTestSource | null | undefined): string | null => {
  if (!source) return null;
  return SOURCE_BADGE_LABEL[source] ?? source;
};

export const getResolutionDisplayLabel = (resolution: DrugTestResolution | null | undefined): string | null => {
  if (!resolution) return null;
  return RESOLUTION_DISPLAY_LABEL[resolution] ?? resolution;
};

export const getResolutionBadgeLabel = (resolution: DrugTestResolution | null | undefined): string | null => {
  if (!resolution) return null;
  return RESOLUTION_BADGE_LABEL[resolution] ?? null;
};

export const getGlanceResolutionLabel = (resolution: DrugTestResolution | null | undefined): string | null => {
  if (!resolution) return null;
  return GLANCE_RESOLUTION_LABEL[resolution] ?? resolution;
};

export const getRecheckLabel = (recheck: DrugTestReport['recheck']): string => {
  if (recheck?.id != null) return `Recheck #${recheck.id}`;
  return 'Initial check';
};

export const getReportCardTitle = (report: DrugTestReport): string => {
  if (report.case_number) {
    const typeName = report.type?.name?.trim();
    return typeName ? `${report.case_number} · ${typeName}` : report.case_number;
  }
  const shortId = report.id.replace(/-/g, '').slice(0, 4);
  return `Report #${shortId}…`;
};

export const getShortReportId = (reportId: string): string => {
  const shortId = reportId.replace(/-/g, '').slice(0, 8);
  return `${shortId}…`;
};

export const getDrugTestTypeLabel = (report: DrugTestReport): string => {
  const name = report.type?.name?.trim();
  if (!name) return EMPTY_VALUE;
  const agency = report.type?.dot_agency?.trim();
  return `${name} (${agency ? 'DOT' : 'Non-DOT'})`;
};

export const getGlanceSourceParts = (
  source: DrugTestSource | null | undefined
): { label: string; enumValue: string } | null => {
  if (!source) return null;
  return {
    label: SOURCE_GLANCE_LABEL[source] ?? source,
    enumValue: source,
  };
};

export const displayOrDash = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : EMPTY_VALUE;
};

/**
 * Report selector line 2 note.
 * - no case number → "No case number yet"
 * - A La Carte → "À la carte" (+ " · Recheck #id" when present)
 * - Native + recheck → "Native · Recheck #id"
 * - Native initial → null
 */
export const getReportCardNote = (report: DrugTestReport): string | null => {
  if (!report.case_number) return NO_CASE_NUMBER_LABEL;

  const recheckPart = report.recheck?.id != null ? `Recheck #${report.recheck.id}` : null;

  if (report.is_a_la_carte) {
    return recheckPart ? `${A_LA_CARTE_NOTE} · ${recheckPart}` : A_LA_CARTE_NOTE;
  }

  if (recheckPart) return `${NATIVE_NOTE} · ${recheckPart}`;

  return null;
};

export const isWithdrawnStatus = (status: string | null | undefined): status is WithdrawnStatus =>
  Boolean(status && (WITHDRAWN_STATUSES as readonly string[]).includes(status));

export const getStatusAlert = (report: DrugTestReport | null): DrugTestStatusAlert | null => {
  if (!report) return null;

  if (report.status === SCHEDULING_ERROR_STATUS) {
    return {
      kind: 'scheduling_error',
      icon: '✕',
      title: SCHEDULING_ERROR_BANNER.title,
      message: SCHEDULING_ERROR_BANNER.message,
      note: SCHEDULING_ERROR_BANNER.note,
    };
  }

  if (report.resolution === 'withdrawn') {
    const statusLabel = report.status?.trim() || null;
    const title = statusLabel ? `${WITHDRAWN_BANNER_TITLE_PREFIX} — ${statusLabel}` : WITHDRAWN_BANNER_TITLE_PREFIX;

    let message = WITHDRAWN_GENERIC_MESSAGE;

    if (statusLabel && isWithdrawnStatus(statusLabel)) {
      const expirationClause = report.expiration ? ` (${formatDateTime(report.expiration)})` : '';
      message = WITHDRAWN_BANNER_COPY[statusLabel]
        .replace(/ ?\(\{expiration when set\}\)/g, expirationClause)
        .replace(/ ?\(\{expiration\}\)/g, expirationClause)
        .replace(/\{case_number\}/g, report.case_number?.trim() || 'unknown');
    }

    return {
      kind: 'withdrawn',
      icon: '⚠',
      title,
      message,
      note: null,
    };
  }

  return null;
};

/** Append `openDrugTestModal=1` to partner-change `redirect_url` (Dashboard decides whether to open). */
export const appendOpenDrugTestModalParam = (redirectUrl: string): string => {
  const url = new URL(redirectUrl);
  url.searchParams.set(OPEN_DRUG_TEST_MODAL_PARAM, '1');
  return url.toString();
};

/**
 * Public API custom domains are not registered as Google OAuth redirect URIs.
 * Flask-Admin login builds `redirect_uri` from the request host (`/callback`),
 * so deep links must use the Heroku host that owns that callback.
 */
const FLASK_ADMIN_BASE_BY_API_HOST: Record<string, string> = {
  'api.turning.io': 'https://prod-turning-rest-api.herokuapp.com',
};

const flaskAdminBaseUrl = (): string => {
  const override = String(import.meta.env.VITE_FLASK_ADMIN_BASE_URL || '').replace(/\/$/, '');
  if (override) return override;

  const apiBase = String(import.meta.env.VITE_API_URL || '').replace(/\/v1\/?$/, '');
  try {
    const host = new URL(apiBase).hostname;
    return FLASK_ADMIN_BASE_BY_API_HOST[host] ?? apiBase;
  } catch {
    return apiBase;
  }
};

/** Flask-Admin details deep link for a drug test report. */
export const getFlaskAdminDrugTestReportUrl = (reportId: string): string => {
  const apiBase = flaskAdminBaseUrl();
  return `${apiBase}/admin/drugtestreport/details/?id=${encodeURIComponent(reportId)}&url=/admin/drugtestreport`;
};

export const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const formatDateTime = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatTime = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

/** Compact table timestamp: `Jul 9, 8:30 AM` (no year). */
export const formatShortDateTime = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/** Days in flight: `5 days` / `1 day`. */
export const getDaysInFlightLabel = (days: number): string => {
  const safe = Number.isFinite(days) ? Math.max(0, Math.trunc(days)) : 0;
  return `${safe} day${safe === 1 ? '' : 's'}`;
};

/** Time in status: `N day(s)` (days only — no threshold). */
export const getTimeInStatusLabel = (timeInStatus: DrugTestTimeInStatus | null | undefined): string | null => {
  if (!timeInStatus) return null;
  return getDaysInFlightLabel(timeInStatus.days);
};

/** Retries: `N (last T)` or bare count. */
export const getRetriesLabel = (retries: DrugTestRetries | null | undefined): string | null => {
  if (!retries) return null;
  if (!retries.last_at) return String(retries.count);
  return `${retries.count} (last ${formatTime(retries.last_at)})`;
};

export type ExpirationTone = 'expired' | 'expiring';

export const getExpirationTone = (
  expiration: string | null | undefined,
  now: Date = new Date()
): ExpirationTone | null => {
  if (!expiration) return null;
  const exp = new Date(expiration);
  if (Number.isNaN(exp.getTime())) return null;

  const nowMs = now.getTime();
  if (exp.getTime() <= nowMs) return 'expired';

  const warningMs = EXPIRATION_WARNING_DAYS * 24 * 60 * 60 * 1000;
  if (exp.getTime() - nowMs <= warningMs) return 'expiring';

  return null;
};
